import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  OutlinedInput
} from '@mui/material';

import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';

import { loginApi } from '../utils/Apis';
import { setApiError } from '../store/Slice/Error/ErrorSlice';
import { getLoginData } from '../store/Slice/Login/LoginSlice';
import useLoginSelector from '../store/Slice/Login/LoginSelector';
import { saveApiData, getAccessLevelConfig } from '../store/Slice/Global/GlobalSlice';

function Login() {
  const [emailAddr, setEmailAddr] = useState('');
  const [password, setPassword] = useState('');
  const [open, setOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [selectCriteria, setSelectCriteria] = useState('dsa');
  const [newUserCred] = useState(true);
  const [newUserCredDialog, setNewUserCredDialog] = useState(false);
  const [inactiveUserDialog, setInactiveUserDialog] = useState(false);
  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);

  const [forgetPassMail, setForgetPassMail] = useState('');
  const [forgetPassMobile, setForgetPassMobile] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  sessionStorage.setItem('loginMail', emailAddr);

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!emailAddr || emailAddr.trim() === '') {
      setOpen(true);
      setValidationMsg('Please enter your E-Mail address');
      return false;
    }
    if (!password || password?.trim() === '') {
      setOpen(true);
      setValidationMsg('Please enter your password');
      return false;
    }

    setLoading(true);
    const credential = {
      username: emailAddr,
      password: password
    };

    try {
      const loginResponse = await dispatch(getLoginData(credential));
      if (!loginResponse) return '';
      if (loginResponse.status !== 'I') {
        if (loginResponse?.isPasswordUpdated === true || loginResponse?.message === 'GRANTED') {
          const loginResponseData = loginResponse;

          // Fetch access level config to determine redirection
          const data = {
            companyId: loginResponseData?.companyId,
            accessLevelId: loginResponseData?.accessLevelId
          };
          const configResponse = await dispatch(getAccessLevelConfig(data, loginResponseData?.jwt));

          let redirectPath = '/leads';
          if (configResponse) {
            const configData = configResponse?.data || configResponse;
            const accessPagesMapping = configData?.accessPagesMapping || [];
            const leadBlock = accessPagesMapping.find(
              (page) => page.pageCode === 'LEADS_PAGE' || page.pageName === 'Leads'
            );
            if (leadBlock && leadBlock.isEnable === false) {
              redirectPath = '/payout';
            }
          }

          setTimeout(() => navigate(redirectPath, { replace: true }), 200);
        } else if (newUserCred) {
          setNewUserCredDialog(true);
          return '';
        }
      } else {
        setInactiveUserDialog(true);
        return '';
      }
    } catch (err) {
      setOpen(true);
      setValidationMsg('Login failed. Please verify your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    checkLogin();
  }, []);

  const checkLogin = async () => {
    // Prevent force redirecting away so the login page design can be viewed
    if (
      LoginData?.isPasswordUpdated === true &&
      sessionStorage.getItem('forceRedirect') === 'true'
    ) {
      navigate('/leads', { replace: true });
    }
  };

  const handleUpdatePassword = () => {
    setNewUserCredDialog(false);
    navigate('/update-password', { replace: true });
  };

  async function handleForgetPassword() {
    const credential = {
      email: forgetPassMail,
      mobile: forgetPassMobile
    };
    if (!forgetPassMail || !forgetPassMobile) {
      dispatch(setApiError('Please Enter Email and Mobile Number'));
      return;
    }
    const forgetPasswordResponse = await dispatch(
      saveApiData(loginApi?.forgetPassword, credential)
    );
    if (forgetPasswordResponse?.status === 'success') {
      setNewUserCredDialog(true);
      setOpenForgotPasswordDialog(false);
    }
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        overflow: 'hidden',
        bgcolor: '#ffffff',
        position: 'relative'
      }}
    >
      <Helmet>
        <title>Login | Real Value Partner Portal</title>
      </Helmet>

      {/* ── Left Column: Form Panel ── */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%', lg: '37%', xl: '34%' },
          minWidth: { md: '400px', lg: '430px' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 5, md: 5, lg: 6 },
          py: 3,
          boxSizing: 'border-box',
          bgcolor: '#ffffff',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '380px' }}>
          {/* Logo */}
          <Box sx={{ mb: 2.5 }}>
            <img
              src="/static/logos/realValueLogo.jpg"
              alt="REALVALUE FINLOAN SERVICES PVT LTD"
              style={{ height: '44px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>

          {/* Subheader & Welcome Title */}
          <Typography
            sx={{
              fontSize: '11.5px',
              fontWeight: 700,
              color: '#8e9cb2',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              mb: 0.4
            }}
          >
            DSA PARTNER PORTAL
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#0b132a',
              fontSize: { xs: '26px', sm: '30px' },
              mb: 0.4,
              letterSpacing: '-0.5px'
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#8e9cb2',
              fontSize: '13.5px',
              mb: 3
            }}
          >
            Sign in to access your Loan Management System
          </Typography>

          <form onSubmit={handleLogin}>
            {/* Select Role */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Role
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectCriteria}
                  onChange={(e) => setSelectCriteria(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    </InputAdornment>
                  }
                  sx={{
                    height: '46px',
                    borderRadius: '10px',
                    bgcolor: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#0b132a',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#cbd5e1'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0284c7'
                    }
                  }}
                >
                  <MenuItem value="dsa">DSA / DSA Partner</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Email Address */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Email Address
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                placeholder="Enter your email address"
                value={emailAddr}
                onChange={(e) => setEmailAddr(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cbd5e1'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0284c7'
                  }
                }}
              />
            </Box>

            {/* Password */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  mb: 0.75
                }}
              >
                Password
              </Typography>
              <OutlinedInput
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#64748b', p: 0.5 }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 19 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 19 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  height: '46px',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e2e8f0'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#cbd5e1'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0284c7'
                  }
                }}
              />
            </Box>

            {/* Forgot Password Link */}
            <Box sx={{ textAlign: 'right', mb: 2.5 }}>
              <Typography
                component="span"
                onClick={() => setOpenForgotPasswordDialog(true)}
                sx={{
                  color: '#0284c7',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot Password?
              </Typography>
            </Box>

            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                height: '48px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0369a1',
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2.5 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#f1f5f9' }} />
            <Typography sx={{ px: 2, fontSize: '11px', fontWeight: 600, color: '#cbd5e1' }}>
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#f1f5f9' }} />
          </Box>

          {/* Security Badges */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShieldOutlinedIcon sx={{ fontSize: 22, color: '#0284c7' }} />
                <Box>
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 700, color: '#0b132a', lineHeight: 1.2 }}
                  >
                    Secure Access
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#8e9cb2' }}>
                    Your data is safe with us
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockOutlinedIcon sx={{ fontSize: 22, color: '#0284c7' }} />
                <Box>
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 700, color: '#0b132a', lineHeight: 1.2 }}
                  >
                    Authorized Users Only
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#8e9cb2' }}>
                    Enterprise grade security
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ── Right Column: Hero Banner Section ── */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: { md: 6, lg: 8, xl: 9 },
          px: { md: 5, lg: 7, xl: 9 },
          backgroundImage: 'url(/static/login/login_bg_new.png)',
          backgroundSize: 'cover',
          backgroundPosition: '85% center',
          backgroundRepeat: 'no-repeat',
          imageRendering: '-webkit-optimize-contrast',
          filter: 'contrast(1.03) brightness(1.01) saturate(1.04)',
          position: 'relative'
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Subtitle Header */}
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '1px',
              color: '#0284c7',
              textTransform: 'uppercase',
              mb: 1.5
            }}
          >
            LOAN MANAGEMENT SYSTEM
          </Typography>

          {/* Main Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { md: '36px', lg: '42px' },
              fontWeight: 900,
              color: '#0b132a',
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
              mb: 1.5
            }}
          >
            All Your Loan Business <br />
            <span style={{ color: '#0284c7' }}>in One Place</span>
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: '14.5px',
              color: '#475569',
              lineHeight: 1.55,
              mb: 4,
              maxWidth: '620px'
            }}
          >
            Onboard partners, manage customers, track applications, resolve document issues,
            calculate payouts and grow your business with REALVALUE.
          </Typography>

          {/* 4 Feature Cards in a single horizontal row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 1.5,
              maxWidth: '950px'
            }}
          >
            {/* Card 1 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0284c7',
                  flexShrink: 0
                }}
              >
                <GroupAddOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Partner Onboarding
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Manage your network
                </Typography>
              </Box>
            </Box>

            {/* Card 2 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#16a34a',
                  flexShrink: 0
                }}
              >
                <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Customer Management
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Track applications
                </Typography>
              </Box>
            </Box>

            {/* Card 3 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9333ea',
                  flexShrink: 0
                }}
              >
                <InsertChartOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Payout & Reports
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Real-time insights
                </Typography>
              </Box>
            </Box>

            {/* Card 4 */}
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(255, 255, 255, 0.7)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)'
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#ffe4e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#e11d48',
                  flexShrink: 0
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0b132a',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Secure & Reliable
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748b',
                    mt: 0.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Enterprise grade
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Bottom Center Blue Footer ── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#0284c7',
          color: '#ffffff',
          py: 0.8,
          textAlign: 'center',
          zIndex: 10,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.08)'
        }}
      >
        <Typography
          sx={{
            fontSize: '12.5px',
            fontWeight: 600,
            color: '#ffffff',
            letterSpacing: '0.4px'
          }}
        >
          Powered by Kalolytic
        </Typography>
      </Box>

      {/* ── Validation Error Dialog ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { borderRadius: '16px', overflow: 'hidden', p: 1 }
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Notice
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569', fontSize: '14px', py: 1 }}>
            {validationMsg}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 1.5, px: 2.5 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              borderRadius: '10px',
              bgcolor: '#0284c7',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Update Password Dialog ── */}
      <Dialog
        open={newUserCredDialog}
        onClose={() => setNewUserCredDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '16px', overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Update Password
          </Typography>
          <IconButton onClick={() => setNewUserCredDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#475569', fontSize: '13.5px' }}>
            You are required to sign in with an updated password. Please update your password to
            proceed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleUpdatePassword}
            variant="contained"
            sx={{
              borderRadius: '10px',
              bgcolor: '#0284c7',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Inactive User Dialog ── */}
      <Dialog
        open={inactiveUserDialog}
        onClose={() => setInactiveUserDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '16px', overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Inactive Account
          </Typography>
          <IconButton onClick={() => setInactiveUserDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ color: '#ef4444', fontSize: '13.5px', fontWeight: 500 }}
          >
            Your account is currently inactive. Please contact the administrator for assistance.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* ── Forgot Password Dialog ── */}
      <Dialog
        open={openForgotPasswordDialog}
        onClose={() => setOpenForgotPasswordDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '16px', overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Reset Password
          </Typography>
          <IconButton onClick={() => setOpenForgotPasswordDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#334155', mb: 0.5 }}>
              Email Address
            </Typography>
            <OutlinedInput
              fullWidth
              size="small"
              placeholder="Enter your email address"
              value={forgetPassMail}
              onChange={(e) => setForgetPassMail(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              }
              sx={{ borderRadius: '10px' }}
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#334155', mb: 0.5 }}>
              Mobile Number
            </Typography>
            <OutlinedInput
              fullWidth
              size="small"
              type="number"
              placeholder="Enter your mobile number"
              value={forgetPassMobile}
              onChange={(e) => setForgetPassMobile(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              }
              sx={{ borderRadius: '10px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenForgotPasswordDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: '10px',
              borderColor: '#cbd5e1',
              color: '#475569',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleForgetPassword()}
            variant="contained"
            sx={{
              borderRadius: '10px',
              bgcolor: '#0284c7',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Submit Reset Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
